import { Badge, Banner, Button, Dialog } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { apiService } from '../services/api';

interface PerfexLead {
  id: string;
  hash: string;
  name: string;
  title: string;
  company: string;
  description: string;
  country: string;
  zip: string;
  city: string;
  state: string;
  address: string;
  assigned: string;
  dateadded: string;
  from_form_id: string;
  status: string;
  source: string;
  lastcontact: string;
  dateassigned: string;
  last_status_change: string;
  addedfrom: string;
  email: string;
  website: string;
  leadorder: string;
  phonenumber: string;
  date_converted: string;
  lost: string;
  junk: string;
  last_lead_status: string;
  is_imported_from_email_integration: string;
  email_integration_uid: string;
  is_public: string;
  default_language: string;
  status_name?: string;
  source_name?: string;
  client_id?: string;
  lead_value?: string;
}

interface PerfexImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EMPTY_FILTERS = { name: '', email: '', company: '', status: '', source: '', country: '', city: '', state: '' };

export function PerfexImportModal({ isOpen, onClose, onSuccess }: PerfexImportModalProps) {
  const toast = useAppToast();
  const [leads, setLeads] = useState<PerfexLead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<PerfexLead[]>([]);
  const [categories, setCategories] = useState<{ id: string; nome: string }[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [uniqueValues, setUniqueValues] = useState({ statuses: [] as string[], sources: [] as string[], countries: [] as string[], cities: [] as string[], states: [] as string[] });

  useEffect(() => { if (isOpen) loadCategories(); }, [isOpen]);

  useEffect(() => {
    let filtered = [...leads];
    if (filters.name) filtered = filtered.filter(l => l.name?.toLowerCase().includes(filters.name.toLowerCase()));
    if (filters.email) filtered = filtered.filter(l => l.email?.toLowerCase().includes(filters.email.toLowerCase()));
    if (filters.company) filtered = filtered.filter(l => l.company?.toLowerCase().includes(filters.company.toLowerCase()));
    if (filters.status) filtered = filtered.filter(l => (l.status_name || l.status) === filters.status);
    if (filters.source) filtered = filtered.filter(l => (l.source_name || l.source) === filters.source);
    if (filters.country) filtered = filtered.filter(l => l.country === filters.country);
    if (filters.city) filtered = filtered.filter(l => l.city === filters.city);
    if (filters.state) filtered = filtered.filter(l => l.state === filters.state);
    setFilteredLeads(filtered);
  }, [filters, leads]);

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      setCategories(response.categories || []);
    } catch {
      toast.error('Erro ao carregar categorias');
    }
  };

  const loadLeads = async () => {
    setIsLoadingLeads(true);
    try {
      const response = await apiService.getPerfexLeads();
      const leadsData: PerfexLead[] = response.leads || [];
      setLeads(leadsData);
      setFilteredLeads(leadsData);
      setUniqueValues({
        statuses: [...new Set(leadsData.map(l => l.status_name || l.status).filter(Boolean))],
        sources: [...new Set(leadsData.map(l => l.source_name || l.source).filter(Boolean))],
        countries: [...new Set(leadsData.map(l => l.country).filter(Boolean))],
        cities: [...new Set(leadsData.map(l => l.city).filter(Boolean))],
        states: [...new Set(leadsData.map(l => l.state).filter(Boolean))],
      });
      if (leadsData.length === 0) toast.info('Nenhum lead encontrado no Perfex CRM');
      else toast.success(`${leadsData.length} leads carregados`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao carregar leads';
      if (msg.includes('não configurado')) toast.error('Configure o Perfex CRM nas Integrações primeiro');
      else toast.error(msg);
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const handleImport = async () => {
    if (selectedLeads.size === 0) { toast.error('Selecione pelo menos um lead'); return; }
    if (!selectedCategoryId) { toast.error('Selecione uma categoria'); return; }
    setIsImporting(true);
    try {
      const response = await apiService.importPerfexLeads(Array.from(selectedLeads), selectedCategoryId);
      toast.success(`${response.imported} importados, ${response.updated} atualizados`);
      onSuccess();
      handleClose();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erro ao importar');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setLeads([]);
    setFilteredLeads([]);
    setSelectedLeads(new Set());
    setSelectedCategoryId('');
    setFilters(EMPTY_FILTERS);
    onClose();
  };

  const toggleLead = (id: string) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o: boolean) => { if (!o) (handleClose)(); }}>
<Dialog size="lg" className="p-6">


      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-semibold mb-1">Importar Leads do Perfex CRM</h3>
          <div className="!text-[var(--text-muted)]">Selecione leads para importar como contatos</div>
        </div>
        <Button variant="secondary" onClick={loadLeads} loading={isLoadingLeads}>
          {leads.length > 0 ? 'Atualizar' : 'Carregar Leads'}
        </Button>
      </div>

      {leads.length === 0 && !isLoadingLeads && (
        <Banner variant="default" className="mb-4" description={<>
          Clique em "Carregar Leads" para buscar leads do Perfex CRM, filtre e selecione os que deseja importar.
        </>} />
      )}

      {/* Filters */}
      {leads.length > 0 && (
        <div className="mb-3 p-3 rounded-md bg-[var(--surface-2)] border border-[var(--border)]">
          <div className="flex justify-between items-center mb-2">
            <div className="font-semibold !text-[var(--text)] !text-sm">Filtros</div>
            <button onClick={() => setFilters(EMPTY_FILTERS)} className="text-xs text-[var(--link)] hover:underline">Limpar</button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {(['name', 'email', 'company'] as const).map((key) => (
              <input
                key={key}
                type="text"
                placeholder={{ name: 'Nome...', email: 'Email...', company: 'Empresa...' }[key]}
                value={filters[key]}
                onChange={(e) => setFilters(f => ({ ...f, [key]: e.target.value }))}
                className="px-2 py-1.5 border border-[var(--border-strong)] rounded text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-text)]"
              />
            ))}
            {(['status', 'source', 'country', 'city', 'state'] as const).map((key) => {
              const options: Record<string, string[]> = { status: uniqueValues.statuses, source: uniqueValues.sources, country: uniqueValues.countries, city: uniqueValues.cities, state: uniqueValues.states };
              const labels: Record<string, string> = { status: 'Status', source: 'Fonte', country: 'País', city: 'Cidade', state: 'Estado' };
              return (
                <select
                  key={key}
                  aria-label={labels[key]}
                  value={filters[key]}
                  onChange={(e) => setFilters(f => ({ ...f, [key]: e.target.value }))}
                  className="px-2 py-1.5 border border-[var(--border-strong)] rounded text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-text)]"
                >
                  <option value="">Todos {labels[key]}</option>
                  {options[key].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              );
            })}
          </div>
        </div>
      )}

      {/* Leads table */}
      {filteredLeads.length > 0 && (
        <div className="mb-4">
          <div className="flex gap-2 mb-2">
            <button onClick={() => setSelectedLeads(new Set(filteredLeads.map(l => l.id)))} className="text-xs text-[var(--link)] hover:underline">
              Selecionar todos ({filteredLeads.length})
            </button>
            <span className="text-[var(--text-faint)]">|</span>
            <button onClick={() => setSelectedLeads(new Set())} className="text-xs text-[var(--link)] hover:underline">
              Desmarcar todos
            </button>
            <div className="!text-[var(--text-faint)] !text-xs ml-auto">{selectedLeads.size} selecionado(s)</div>
          </div>
          <div className="border border-[var(--border)] rounded-md overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface-2)] border-b border-[var(--border)] sticky top-0">
                <tr>
                  {['', 'Nome', 'Email', 'Telefone', 'Empresa', 'Status', 'Fonte'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-[var(--text)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className={`cursor-pointer hover:bg-[var(--surface-2)] ${selectedLeads.has(lead.id) ? 'bg-[var(--tint-green)]' : ''}`}
                    onClick={() => toggleLead(lead.id)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${lead.name || lead.id}`}
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => toggleLead(lead.id)}
                        className="rounded"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-[var(--text)]">{lead.name || '-'}</td>
                    <td className="px-3 py-2 text-[var(--text-muted)]">{lead.email || '-'}</td>
                    <td className="px-3 py-2 text-[var(--text-muted)]">{lead.phonenumber || '-'}</td>
                    <td className="px-3 py-2 text-[var(--text-muted)]">{lead.company || '-'}</td>
                    <td className="px-3 py-2">
                      {(lead.status_name || lead.status) && (
                        <Badge variant="blue">{lead.status_name || lead.status}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-muted)]">{lead.source_name || lead.source || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer */}
      {leads.length > 0 && (
        <div className="pt-4 border-t border-[var(--border)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="font-semibold !text-[var(--text)] whitespace-nowrap">Categoria de destino:</div>
            <select
              aria-label="Categoria de destino"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-[var(--border-strong)] rounded text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-text)]"
            >
              <option value="">Selecione uma categoria</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
            </select>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={handleClose}>Fechar</Button>
            <Button variant="primary" onClick={handleImport} disabled={selectedLeads.size === 0 || !selectedCategoryId || isImporting} loading={isImporting}>
              Importar {selectedLeads.size > 0 ? `${selectedLeads.size} Contato(s)` : ''}
            </Button>
          </div>
        </div>
      )}
    
</Dialog>
</Dialog.Root>
  );
}
